import { Link } from "@remix-run/react";
import {
  ArrowRight,
  Award,
  ChevronLeft,
  ChevronRight,
  Eye,
  Flame,
  MessageSquare,
  MessageSquarePlus,
  Megaphone,
  Pin,
  Reply,
  ThumbsUp,
  Trophy,
  Users,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  CommunityBadgeIcon,
  CommunityBadgePill,
  getCommunityBadgeStyles,
} from "#app/components/community/badge-display.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "#app/components/ui/avatar";
import { Badge } from "#app/components/ui/badge.tsx";
import { Card, CardContent } from "#app/components/ui/card-legacy.tsx";
import {
  CATEGORY_BADGE_STYLES,
  getCommunityAvatarFallbackClassName,
  getCommunityAvatarFallbackLabel,
  formatBadgeAwardLine,
  formatCommunityDate,
  formatCompactNumber,
  getCommunityDisplayName,
  stripForumMarkdownForPreview,
} from "#app/utils/community.shared.ts";
import { cn, getUserImgSrc } from "#app/utils/misc.tsx";

function CommunityAnnouncementBanner({
  announcement,
}: {
  announcement?: any | null;
}) {
  const { t } = useTranslation("community");
  if (!announcement) return null;

  const getVariantStyles = (variant?: string) => {
    switch (variant) {
      case "info":
        return {
          card: "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/40",
          iconBg:
            "bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-300",
          badge:
            "border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-950/40",
          btn: "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/60",
        };
      case "success":
        return {
          card: "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/40",
          iconBg:
            "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300",
          badge:
            "border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-950/40",
          btn: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/60",
        };
      case "error":
        return {
          card: "border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/40",
          iconBg:
            "bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-300",
          badge:
            "border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-950/40",
          btn: "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/60",
        };
      case "brand":
        return {
          card: "border-primary/20 bg-primary/5 dark:border-zinc-700 dark:bg-zinc-900/80",
          iconBg:
            "bg-primary/10 text-primary dark:bg-zinc-800 dark:text-zinc-100",
          badge:
            "border-primary/20 text-primary bg-primary/10 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100",
          btn: "bg-primary/10 text-primary hover:bg-primary/20 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700",
        };
      case "warning":
      default:
        return {
          card: "border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/40",
          iconBg:
            "bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-300",
          badge:
            "border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-950/40",
          btn: "bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-950/60",
        };
    }
  };

  const styles = getVariantStyles(announcement.variant);

  return (
    <Card
      className={cn(
        "overflow-hidden border-0",
        "bg-[url('/img/Announcement.png')] bg-cover bg-center",
        // Light artwork stays light — keep dark ink in both themes for contrast.
        "dark:bg-none dark:bg-linear-to-br dark:from-amber-950 dark:via-orange-950/80 dark:to-zinc-900",
        "dark:ring-1 dark:ring-orange-800/50",
      )}
    >
      <CardContent className="flex min-h-0 min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/70 dark:bg-orange-950/70">
            <Megaphone className="h-5 w-5 text-orange-800 dark:text-orange-300" />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="min-w-0 max-w-full wrap-break-word text-base font-semibold leading-snug text-slate-900 line-clamp-2 dark:text-orange-50">
                {announcement.title}
              </h3>
              {announcement.pinned ? (
                <Badge
                  variant="outline"
                  className={cn("shrink-0 border", styles.badge)}
                >
                  <Pin className="mr-1 h-3 w-3" />
                  {t("landing.pinned")}
                </Badge>
              ) : null}
            </div>
            <p className="text-sm text-slate-700 line-clamp-2 dark:text-orange-100/90">
              {announcement.summary || announcement.body}
            </p>
          </div>
        </div>
        {announcement.ctaUrl ? (
          <Link
            to={announcement.ctaUrl}
            className="inline-flex shrink-0 items-center justify-center gap-1 self-start rounded-xl border-none bg-white/90 px-3 py-2 text-sm font-medium text-orange-900 shadow-none ring-1 ring-orange-900/10 hover:bg-white sm:self-center dark:bg-orange-900/80 dark:text-orange-50 dark:ring-orange-500/30 dark:hover:bg-orange-900"
          >
            {announcement.ctaLabel || t("landing.readMore")}
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CommunityWinnerSpotlight({ challenge }: { challenge?: any | null }) {
  const { t } = useTranslation("community");
  if (!challenge?.winnerTopic) return null;

  const winnerName = getCommunityDisplayName(challenge.winnerTopic.author);
  const topicHref = `/u/community/topic/${challenge.winnerTopic.id}`;
  const announcementHref = challenge.winnerAnnouncement?.id
    ? `/u/community/announcement/${challenge.winnerAnnouncement.id}`
    : null;

  return (
    <Card
      className={cn(
        "overflow-hidden border-0",
        "bg-linear-to-br from-emerald-50 via-teal-50/80 to-white",
        "ring-1 ring-emerald-200/70",
        "dark:from-emerald-950 dark:via-teal-950/70 dark:to-zinc-900",
        "dark:ring-emerald-800/50",
      )}
    >
      <CardContent className="flex min-h-0 min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/85 text-emerald-800 ring-1 ring-emerald-200/60 dark:bg-emerald-900/60 dark:text-emerald-200 dark:ring-emerald-500/30">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="min-w-0 max-w-full wrap-break-word text-base font-semibold leading-snug text-slate-900 line-clamp-2 dark:text-emerald-50">
                {challenge.title}
              </h3>
              <Badge
                variant="outline"
                className="shrink-0 border-emerald-200 dark:border-emerald-700 bg-emerald-100 dark:bg-emerald-950/60 text-[10px] font-normal text-emerald-800 dark:text-emerald-200"
              >
                {t("landing.winner")}
              </Badge>
            </div>
            <p className="text-sm text-slate-700 line-clamp-2 dark:text-emerald-100/90">
              <span className="font-medium text-slate-800 dark:text-emerald-50">
                {winnerName}
              </span>
              {" — "}
              {challenge.winnerTopic.title}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-center sm:justify-end">
          <Link
            to={topicHref}
            className="inline-flex items-center gap-1 rounded-xl border-none bg-white/90 px-3 py-2 text-sm font-medium text-emerald-900 shadow-none ring-1 ring-emerald-900/10 hover:bg-white dark:bg-emerald-900/80 dark:text-emerald-50 dark:ring-emerald-500/30 dark:hover:bg-emerald-900"
          >
            {t("landing.viewWinningEntry")}
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
          {announcementHref ? (
            <Link
              to={announcementHref}
              className="inline-flex items-center gap-1 rounded-xl border-none bg-white/90 px-3 py-2 text-sm font-medium text-emerald-900 shadow-none ring-1 ring-emerald-900/10 hover:bg-white dark:bg-emerald-900/80 dark:text-emerald-50 dark:ring-emerald-500/30 dark:hover:bg-emerald-900"
            >
              {t("landing.announcement")}
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            </Link>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function CommunityChallengeCard({ challenge }: { challenge?: any | null }) {
  const { t } = useTranslation("community");
  if (!challenge) return null;

  const targetUrl =
    challenge.ctaUrl || `/u/community/challenge/${challenge.id}`;

  return (
    <Card
      className={cn(
        "overflow-hidden border-0",
        "bg-[url('/img/Challenge.png')] bg-cover bg-center",
        "dark:bg-none dark:bg-linear-to-br dark:from-indigo-950 dark:via-violet-950/70 dark:to-zinc-900",
        "dark:ring-1 dark:ring-indigo-800/50",
      )}
    >
      <CardContent className="flex min-h-0 min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="min-w-0 max-w-full wrap-break-word text-base font-semibold leading-snug text-slate-900 line-clamp-2 dark:text-indigo-50">
                {challenge.title}
              </h3>
              <Badge
                variant="outline"
                className="shrink-0 border-indigo-200 dark:border-indigo-700 bg-indigo-100 dark:bg-indigo-950/60 text-[10px] font-normal text-indigo-700 dark:text-indigo-200"
              >
                {t("landing.challenge")}
              </Badge>
            </div>
            <p className="text-sm text-slate-700 line-clamp-2 dark:text-indigo-100/90">
              {challenge.summary || challenge.description}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-center sm:justify-end">
          {challenge.topicId ? (
            <Link
              to={`/u/community/topic/${challenge.topicId}`}
              className="inline-flex items-center gap-1 rounded-xl bg-white/90 px-3 py-2 text-sm font-medium text-indigo-800 ring-1 ring-indigo-900/10 hover:bg-white dark:bg-indigo-900/80 dark:text-indigo-50 dark:ring-indigo-500/30 dark:hover:bg-indigo-900"
            >
              <MessageSquare className="h-4 w-4 shrink-0" aria-hidden />
              {t("landing.discussion")}
            </Link>
          ) : null}
          <Link
            to={targetUrl}
            className="inline-flex items-center gap-1 rounded-xl bg-white/90 px-3 py-2 text-sm font-medium text-indigo-800 ring-1 ring-indigo-900/10 hover:bg-white dark:bg-indigo-900/80 dark:text-indigo-50 dark:ring-indigo-500/30 dark:hover:bg-indigo-900"
          >
            {challenge.ctaLabel || t("landing.viewChallenge")}
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

const HERO_CAROUSEL_INTERVAL_MS = 8000;

function CommunityHeroCarousel({
  ariaLabel,
  slideKeys,
  renderSlide,
}: {
  ariaLabel: string;
  slideKeys: string[];
  renderSlide: (index: number) => ReactNode;
}) {
  const { t } = useTranslation("community");
  const count = slideKeys.length;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (count <= 1) return;
    if (typeof window.matchMedia === "function") {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      if (reduceMotion.matches) return;
    }
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, HERO_CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [count, index]);

  if (count === 0) return null;

  const multi = count > 1;
  const go = (next: number) => {
    setIndex(((next % count) + count) % count);
  };

  return (
    <div
      className="relative w-full"
      role="region"
      aria-label={ariaLabel}
      aria-roledescription="carousel"
    >
      <div
        className={cn("flex w-full items-stretch", multi && "gap-2 sm:gap-3")}
      >
        {multi ? (
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={t("landing.previousSlide")}
            onClick={() => go(index - 1)}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
        ) : null}

        <div
          className={cn("min-w-0 overflow-hidden", multi ? "flex-1" : "w-full")}
        >
          <div
            className="flex transition-transform duration-300 ease-out motion-reduce:transition-none"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {slideKeys.map((key, i) => (
              <div
                key={key}
                className="min-w-full max-w-full shrink-0 overflow-hidden"
                role="group"
                aria-roledescription="slide"
                aria-hidden={i !== index}
                aria-label={t("landing.slideOf", {
                  current: i + 1,
                  total: count,
                })}
              >
                {renderSlide(i)}
              </div>
            ))}
          </div>
        </div>

        {multi ? (
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={t("landing.nextSlide")}
            onClick={() => go(index + 1)}
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
        ) : null}
      </div>

      {multi ? (
        <div
          className="flex justify-center gap-1.5 pt-3"
          role="tablist"
          aria-label={t("landing.slidesLabel", { label: ariaLabel })}
        >
          {slideKeys.map((key, i) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t("landing.goToSlide", { number: i + 1 })}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === index
                  ? "w-6 bg-slate-700"
                  : "w-1.5 bg-slate-300 hover:bg-slate-400",
              )}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function CommunityAnnouncementBannerCarousel({
  announcements,
}: {
  announcements: any[];
}) {
  const { t } = useTranslation("community");
  if (!announcements?.length) return null;

  return (
    <CommunityHeroCarousel
      ariaLabel={t("landing.carouselAnnouncements")}
      slideKeys={announcements.map((a) => String(a.id))}
      renderSlide={(i) => (
        <CommunityAnnouncementBanner announcement={announcements[i]} />
      )}
    />
  );
}

export function CommunityChallengeCardCarousel({
  challenges,
}: {
  challenges: any[];
}) {
  const { t } = useTranslation("community");
  if (!challenges?.length) return null;

  return (
    <CommunityHeroCarousel
      ariaLabel={t("landing.carouselChallenges")}
      slideKeys={challenges.map((c) => String(c.id))}
      renderSlide={(i) => <CommunityChallengeCard challenge={challenges[i]} />}
    />
  );
}

export function CommunityWinnerSpotlightCarousel({
  challenges,
}: {
  challenges: any[];
}) {
  const { t } = useTranslation("community");
  const items = challenges.filter((c) => c?.winnerTopic);
  if (!items.length) return null;

  return (
    <CommunityHeroCarousel
      ariaLabel={t("landing.carouselWinners")}
      slideKeys={items.map((c) => String(c.id))}
      renderSlide={(i) => <CommunityWinnerSpotlight challenge={items[i]} />}
    />
  );
}

export function CommunityFeedItem({ topic }: { topic: any }) {
  const { t } = useTranslation("community");
  return (
    <Link
      to={`topic/${topic.id}`}
      className="block rounded-xl border border-gray-200 bg-[#FCFCFC] p-4 transition hover:shadow-md hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900/70 dark:hover:border-slate-600"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap">
          <div className="flex items-center shrink-0">
            <Badge
              variant="outline"
              className={cn(
                "border font-normal",
                CATEGORY_BADGE_STYLES[topic.category.slug] ||
                  CATEGORY_BADGE_STYLES.general,
              )}
            >
              {topic.category.name}
            </Badge>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {topic.title}
            </h3>
          </div>

          <div className="right-actions flex items-center justify-end gap-5 shrink-0">
            <div className="flex items-center gap-2 shrink-0">
              {topic.isSolved ? (
                <Badge
                  variant="outline"
                  className="bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                >
                  {t("landing.solved")}
                </Badge>
              ) : null}
              {topic.isLocked ? (
                <Badge variant="secondary">{t("landing.locked")}</Badge>
              ) : null}
              {topic.workflowSnapshot ? (
                <Badge variant="secondary">{t("landing.context")}</Badge>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center text-xs divide-x divide-zinc-300 text-slate-500 dark:divide-zinc-700 dark:text-slate-400">
              <div className="flex items-center gap-1.5 px-2">
                <ThumbsUp className="h-4 w-4" />
                <span>{formatCompactNumber(topic.score)}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2">
                <MessageSquare className="h-4 w-4" />
                <span>{formatCompactNumber(topic._count.posts)}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2">
                <Eye className="h-4 w-4" />
                <span>{formatCompactNumber(topic.viewCount)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="">
          {topic.content && (
            <p className="line-clamp-2 text-sm text-slate-500 mb-4 dark:text-slate-400">
              {stripForumMarkdownForPreview(topic.content)}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Avatar className="h-6 w-6">
              <AvatarImage
                src={getUserImgSrc(topic.author.image?.id) || undefined}
              />
              <AvatarFallback
                className={getCommunityAvatarFallbackClassName(topic.author)}
              >
                {getCommunityAvatarFallbackLabel(topic.author)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {getCommunityDisplayName(topic.author)}
            </span>
            {/* <span>•</span> */}
            <span>{formatCommunityDate(topic.createdAt)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function CommunityLeaderboard({ items }: { items: any[] }) {
  const { t } = useTranslation("community");
  return (
    <Card className="rounded-xl border border-gray-200 bg-[#FCFCFC] p-5 shadow-none dark:border-slate-800 dark:bg-slate-900/70">
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t("sidebar.topSolvers")}
        </h3>
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="text-sm text-slate-500">
              {t("sidebar.noLeaderboard")}
            </div>
          ) : (
            items.map((item, index) => (
              <div
                key={item.user.id}
                className="flex items-center justify-between gap-3 py-1"
              >
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-4 text-center font-medium text-slate-400">
                    {index + 1}
                  </div>
                  <Avatar className="h-6 w-6">
                    <AvatarImage
                      src={getUserImgSrc(item.user.image?.id) || undefined}
                    />
                    <AvatarFallback
                      className={getCommunityAvatarFallbackClassName(item.user)}
                    >
                      {getCommunityAvatarFallbackLabel(item.user)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {getCommunityDisplayName(item.user)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <Award className="h-4 w-4 text-yellow-500 dark:text-yellow-300" />
                  {formatCompactNumber(item.score)} XP
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}

export function CommunityRecentEngagement({ items }: { items: any[] }) {
  const { t } = useTranslation("community");
  return (
    <Card className="rounded-xl border border-gray-200 bg-[#FCFCFC] p-5 shadow-none dark:border-slate-800 dark:bg-slate-900/70">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t("sidebar.recentPostActivity")}
        </h3>
        {items.length === 0 ? (
          <div className="text-sm text-slate-500">{t("sidebar.noPosts")}</div>
        ) : (
          <div
            className="max-h-60 space-y-1 overflow-y-auto overscroll-y-contain pr-1 [-webkit-overflow-scrolling:touch] divide-y divide-zinc-200 dark:divide-zinc-800"
            aria-label="Scroll for more recent post activity"
          >
            {items.map((item) => (
              <Link
                key={`${item.kind}-${item.id}`}
                to={`/u/community/topic/${item.topicId}`}
                className="flex gap-3  py-2.5 transition-colors hover:bg-slate-50 -mx-1 px-1 dark:hover:bg-slate-800/70"
              >
                <div className="relative shrink-0">
                  <Avatar className="h-6 w-6">
                    <AvatarImage
                      src={getUserImgSrc(item.author.image?.id) || undefined}
                    />
                    <AvatarFallback
                      className={cn(
                        "text-sm",
                        getCommunityAvatarFallbackClassName(item.author),
                      )}
                    >
                      {getCommunityAvatarFallbackLabel(item.author)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex h-5 w-5 mt-1.5 ml-0.5 items-center justify-center rounded-full border border-white bg-slate-200 text-slate-700 dark:border-slate-800 dark:bg-slate-700 dark:text-slate-200">
                    {item.kind === "topic" ? (
                      <MessageSquarePlus className="h-2.5 w-2.5" />
                    ) : (
                      <Reply className="h-2.5 w-2.5" />
                    )}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {getCommunityDisplayName(item.author)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {item.kind === "topic"
                        ? t("sidebar.started")
                        : t("sidebar.repliedIn")}
                    </span>
                  </div>
                  {item.kind === "topic" ? (
                    <p className="line-clamp-2 text-sm text-slate-700 dark:text-slate-300">
                      {item.title}
                    </p>
                  ) : (
                    <p className="line-clamp-2 text-sm text-slate-700 dark:text-slate-300">
                      <span className="text-slate-500">{item.topicTitle}</span>
                      {item.snippet ? (
                        <>
                          <span className="text-slate-400"> · </span>
                          {item.snippet}
                        </>
                      ) : null}
                    </p>
                  )}
                  <div className="mt-0.5 text-xs text-slate-400">
                    {formatCommunityDate(item.createdAt)}
                    {item.kind === "topic" ? (
                      <span className="text-slate-600 dark:text-slate-300">
                        {" "}
                        · {item.categoryName}
                      </span>
                    ) : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function CommunityBadgeFeed({ badges }: { badges: any[] }) {
  const { t } = useTranslation("community");
  return (
    <Card className="rounded-xl border border-gray-200 bg-[#FCFCFC] p-5 shadow-none dark:border-slate-800 dark:bg-slate-900/70">
      <div className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-900 dark:text-slate-100">
          {t("sidebar.recentBadgeRewards")}
        </h3>
        <div className="space-y-3">
          {badges.length === 0 ? (
            <div className="text-sm text-slate-500">
              {t("sidebar.noBadges")}
            </div>
          ) : (
            badges.map((badge) => {
              const badgeStyles = getCommunityBadgeStyles(badge.badge);

              return (
                <div key={badge.id} className="flex items-center gap-3 py-2">
                  <div
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg",
                      badgeStyles.iconWrap,
                    )}
                  >
                    <CommunityBadgeIcon
                      icon={badge.badge.icon}
                      className="h-5 w-5"
                    />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {getCommunityDisplayName(badge.user)}
                      </div>
                      <CommunityBadgePill
                        badge={badge.badge}
                        className="text-[11px]"
                      />
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatBadgeAwardLine(badge.badge.name, badge.reason)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
}

export function CommunityStatsRail({
  stats,
  topContributors = [],
}: {
  stats: any;
  topContributors?: any[];
}) {
  const { t } = useTranslation("community");
  const sessionCount = stats.usersWithValidSession ?? 0;
  const items = [
    {
      label: t("sidebar.active30d"),
      value: stats.activeContributors,
      icon: Users,
      title: t("sidebar.active30dTitle"),
    },
    {
      label: t("sidebar.topics"),
      value: stats.totalTopics,
      icon: MessageSquare,
      title: t("sidebar.topicsTitle"),
    },
    {
      label: t("sidebar.replies"),
      value: stats.totalReplies,
      icon: Reply,
      title: t("sidebar.repliesTitle"),
    },
    {
      label: t("sidebar.signedInSessions"),
      value: sessionCount,
      icon: Flame,
      title: t("sidebar.signedInSessionsTitle"),
    },
  ];

  const displayContributors = topContributors.slice(0, 3);
  const remaining = stats.activeContributors - displayContributors.length;

  return (
    <Card className="rounded-xl border border-gray-200 bg-[#FCFCFC] p-5 shadow-none dark:border-slate-800 dark:bg-slate-900/70">
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t("sidebar.communityStats")}
        </h3>

        <div className="min-h-10 pt-2">
          {displayContributors.length > 0 ? (
            <div className="flex -space-x-2 overflow-hidden">
              {displayContributors.map((contributor, i) => (
                <Avatar
                  key={contributor.user.id || i}
                  className="inline-block h-7 w-7 rounded-full ring-2 ring-white dark:ring-slate-800"
                >
                  <AvatarImage
                    src={getUserImgSrc(contributor.user.image?.id) || undefined}
                  />
                  <AvatarFallback
                    className={cn(
                      "text-sm",
                      getCommunityAvatarFallbackClassName(contributor.user),
                    )}
                  >
                    {getCommunityAvatarFallbackLabel(contributor.user)}
                  </AvatarFallback>
                </Avatar>
              ))}
              {remaining > 0 && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 ring-2 ring-white text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-900">
                  +{formatCompactNumber(remaining)}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              {t("sidebar.noTopContributors")}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {items.map((item) => (
            <div key={item.label} className="space-y-1" title={item.title}>
              <div className="text-xs text-slate-500">{item.label}</div>
              <div className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {formatCompactNumber(item.value)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
